import React, { FC, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Result, Table, TableProps } from 'antd';
import {
    getSingleScenarioTableColumns,
    transformFilterObjectWithDimensionsInfo,
} from 'utils/getPricingSimulatorTableColumns';
import { useFillHeightToFitScreen } from 'hooks/general/useFillHeightToFitScreen';
import { colorConstants } from 'config/colorConstants';
import styled, { StyledComponent } from '@emotion/styled';
import { pricingSimulatorDataContext } from 'contexts/PricingSimulatorContext';
import ScenarioColumnEditModal from './Modals/ScenarioColumnEditModal';
import Loader from '../Loader';
import { useCommonMutationHandler } from 'hooks/general/useCommonMutationHandler';
import { pricingSimulatorUrls } from 'config/urls';
import InfiniteScroller from '../InfiniteScroller';
import { UserContext } from 'contexts/ConfigContext';
import { generateErrorMsg, getConfig, getKpiAttributeConfig, sendNotification } from 'utils/helperMethods';
import ErrorBox from '../ErrorBox/ErrorBox';
import { useDebounceLoading } from 'hooks/general/useDebounceLoading';
import { ConfigKeys, PricingSimulatorNotificationMessages } from 'utils/Enums';
import { Theme } from '@emotion/react';
import dayjs from 'dayjs';
import { BulkUploadInfo, LenseAttribute, LenseConfig, Project, Scenario } from 'types/pricingSimulator';
import { pricingSimulatorWrapperDataContext } from 'contexts/PricingSimulatorWrapperContext';

type Props = {
    widthConfig: {
        dimSection: number | string;
        lensesSection: number | string;
    };
    commonLoader: boolean;
};

export const CustomTable = styled(Table)<{ dimLength?: number }>`
    // .ant-table-sticky-scroll {
    //     position: static;
    // }
    .ant-table-body {
        overflow: hidden;
    }
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        // padding: 0px;
        // height: 68px;
    }
    .ant-table-tbody > tr > td:last-child {
        border-right: none !important;
    }
    .ant-table-body col:nth-child(n + ${(props: any) => props?.dimLength || 4}) {
        min-width: 204px;
    }
    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }

    border: ${'1px solid #DBDFE9'};
`;

export type EditModalType = {
    isOpen: boolean;
    from: 'cell' | 'column';
    data: any;
};

const modalInitialState: EditModalType = {
    isOpen: false,
    data: {},
    from: 'cell',
};

const pricingSimulatorTableId = 'pricing-simulator-scenario-table';
const scenarioTableContainerId = 'scenario-table-container';

const ScenarioTableWrapper: FC<Props> = ({ widthConfig, commonLoader }) => {
    const [handleEditModal, setHandleEditModal] = useState<EditModalType>({ ...modalInitialState });
    const [sortPopupKey, setSortPopupKey] = useState(null);
    const mainSectionHeight = useRef<number | null>(0);

    const { appConfig } = useContext(UserContext);
    const pricingSimulatorConfig = getConfig('pricingSimulator', appConfig);
    const commonConfig = getConfig(ConfigKeys.COMMON_CONFIG, appConfig);
    const { decimalForNonPercentage, decimalForPercentage } = commonConfig;
    const {
        selectedScenarioInfo,
        scenarioDataLoading,
        refetchScenarioTableData,
        dimensionCheckboxObj,
        setDimensionCheckboxObj,
        isDropdownAtLowestLevel,
        updateScenarioProperty,
        viewMode,
        applyFilterObject,
        showPercentageChange,
        setSelectedProjectInfo,
        defaultScenarioTableColumnsLength,
        selectedDimensionListValues,
        showIsEditedRecords,
        scrollTopValue,
        setScrollTopValue,
    } = useContext(pricingSimulatorDataContext);

    const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);
    const scenarioFilterKeys = dimensionConfigData?.scenarioFilterKeys ?? [];
    const enableMicroInfoForTables = pricingSimulatorConfig.enableMicroInfoForTables;

    //this hook with give height to fill the screen height
    mainSectionHeight.current = useFillHeightToFitScreen({
        id: scenarioTableContainerId,
        height: mainSectionHeight.current,
        buffer: 110,
        triggerDummyRender: true,
    });

    function handleInputModalChange(modalData: EditModalType) {
        setHandleEditModal({
            isOpen: modalData.isOpen,
            data: modalData.data,
            from: modalData.from,
        });
    }

    const { loading: commonLoading } = useDebounceLoading({
        loadingDependency: commonLoader,
    });

    //infinite scrollable element
    const tableScrollableElement = useMemo(() => {
        const tableScrollableElement = document
            .getElementById(scenarioTableContainerId)
            ?.getElementsByClassName('infinite-scroll-component')[0];
        return tableScrollableElement;
    }, [selectedScenarioInfo?.data]);

    const lensKpiTypeMapping = useMemo(() => {
        const kpiIdMapping: any = {};
        selectedScenarioInfo?.lenseConfig?.forEach((lens: LenseConfig) => {
            lens.attributes.forEach((kpi: LenseAttribute) => {
                kpiIdMapping[kpi.attributeId] = kpi.kpiTypeId;
            });
        });

        return kpiIdMapping;
    }, [selectedScenarioInfo]);

    //for default view only display checkbox,dim1 and dim2
    const isDefaultView =
        Array.isArray(selectedScenarioInfo?.data) &&
        Object.keys(selectedScenarioInfo?.data?.[0] || {}).length <= defaultScenarioTableColumnsLength &&
        ((selectedScenarioInfo?.offset || 0) > 0 ? true : !commonLoading) &&
        !selectedScenarioInfo?.error;
    const memoisedColumns = useMemo(() => {
        const predefinedColumns = [{ label: '', key: 'checkbox', sortKey: '' }];
        selectedScenarioInfo?.dimAttributes?.forEach((item) => {
            predefinedColumns.push({
                label: item?.label || '',
                key: item?.value || '',
                sortKey: item?.value || '',
            });
        });
        const sortOrder = selectedScenarioInfo?.sortObject?.sortingOrder;
        return getSingleScenarioTableColumns(
            selectedScenarioInfo?.data,
            predefinedColumns,
            defaultScenarioTableColumnsLength,
            dimensionCheckboxObj,
            setDimensionCheckboxObj,
            widthConfig.dimSection,
            handleInputModalChange,
            handleEditModal,
            true,
            viewMode,
            showPercentageChange,
            {
                sortPopupKey,
                sortBy: selectedScenarioInfo?.sortObject?.sortBy || 0,
                sortType: selectedScenarioInfo?.sortObject?.sortType || 0,
                sortOrder: sortOrder === 'asc' ? 'ascend' : sortOrder === 'desc' ? 'descend' : null,
            },
            setSortPopupKey,
            updateScenarioProperty,
            selectedDimensionListValues,
            decimalForNonPercentage,
            decimalForPercentage,
            lensKpiTypeMapping,
            enableMicroInfoForTables,
        );
    }, [
        dimensionCheckboxObj,
        widthConfig.dimSection,
        handleEditModal,
        selectedScenarioInfo?.data,
        showPercentageChange,
        sortPopupKey,
    ]);

    const { mutate: editKpiMutateFn, loading: editKpiLoading } = useCommonMutationHandler({
        url: pricingSimulatorUrls.editKpiUrl,
        method: 'post',
    });

    function onEditSuccess(data: any, variables: any) {
        sendNotification('success', PricingSimulatorNotificationMessages.EDIT_SCENARIO_COLUMN_SUCCESS);
        setHandleEditModal({ ...modalInitialState });

        const updatedAtText = dayjs.unix(data?.updated_at).fromNow();

        //update status and last updated at
        setSelectedProjectInfo((prevScenarioInfo: Project | null) => {
            let tempScenarioInfo: any = prevScenarioInfo || {};
            const newScenarios = tempScenarioInfo?.simulationList?.map((item: Scenario) => {
                return {
                    ...item,
                    status: Number(variables?.scenario_id) === item?.scenarioId ? data?.status : item?.status,
                    updatedAt: Number(variables?.scenario_id) === item?.scenarioId ? updatedAtText : item?.updatedAt,
                };
            });
            const parsedData = {
                ...tempScenarioInfo,
                simulationList: newScenarios || [],
            };
            return parsedData;
        });
        updateScenarioProperty('status', data?.status);
        updateScenarioProperty('updatedAt', updatedAtText);
        //reset checkbox
        setDimensionCheckboxObj({});

        //refetch the scenario table data
        refetchScenarioTableData();
    }

    function onEditError(error: any) {
        const detail = error?.response?.data?.detail;

        let errorMsg: string;

        if (Array.isArray(detail?.errors)) {
            // Multiple validation errors
            errorMsg = detail.errors.join('\n');
        } else if (typeof detail === 'object' && detail?.message) {
            // Structured error with message and optional errors
            errorMsg = detail.message;
            if (Array.isArray(detail.errors)) {
                errorMsg += '\n' + detail.errors.join('\n');
            }
        } else {
            // Fallback to generic error message
            errorMsg = generateErrorMsg(String(detail), PricingSimulatorNotificationMessages.GENERALIZED_ERROR_MSG);
        }

        // Show error message
        sendNotification('error', errorMsg);
    }

    //called when clicked on apply button in edit popup
    const onEditApply = (data: any) => {
        if (selectedScenarioInfo?.bulkUploadInfo?.status === 'IN_PROGRESS') {
            sendNotification(
                'error',
                'Bulk upload in progress. Updates are temporarily disabled. Please try again once the upload is complete.',
            );
            return;
        }

        const filters = transformFilterObjectWithDimensionsInfo(applyFilterObject, scenarioFilterKeys);
        const config = getKpiAttributeConfig(data?.kpiData?.kpiId, selectedScenarioInfo?.lenseConfig || []);
        let payload = {
            lense_id: selectedScenarioInfo?.activeLense?.activeLenseId,
            scenario_id: selectedScenarioInfo?.scenarioId,
            kpi_id: data?.kpiData?.kpiId,
            search_keyword: selectedScenarioInfo?.search || '',
            dimension_attributes: selectedDimensionListValues,
            filters: filters,
            change_type: data?.inputTile?.value,
            value: Number(data?.value),
            row_ids: [],
            is_lowest: isDropdownAtLowestLevel,
            user_name: localStorage.getItem('email'),
            edited_records: showIsEditedRecords,
            row_filters: [],
            has_kpi_rules_validations: config?.hasKpiRulesValidations,
        };
        //all
        if (data?.applyValue === 'all') {
            editKpiMutateFn(
                { ...payload },
                {
                    onSuccess: (data) => {
                        onEditSuccess(data?.data, payload);
                        const newBulkUploadInfo: BulkUploadInfo = {
                            status: undefined,
                            errorObject: {},
                        };
                        updateScenarioProperty('bulkUploadInfo', newBulkUploadInfo);
                    },
                    onError: (error) => {
                        //show error message
                        onEditError(error);
                    },
                },
            );
            //cell
        } else if (data?.applyValue === 'cell') {
            editKpiMutateFn(
                {
                    ...payload,
                    row_ids: isDropdownAtLowestLevel ? [data?.kpiData?.cellId] : [],
                    row_filters: !isDropdownAtLowestLevel ? [data?.kpiData?.attributeNames] : [],
                },
                {
                    onSuccess: (data) => {
                        onEditSuccess(data?.data, payload);
                        const newBulkUploadInfo: BulkUploadInfo = {
                            status: undefined,
                            errorObject: {},
                        };
                        updateScenarioProperty('bulkUploadInfo', newBulkUploadInfo);
                    },
                    onError: (error) => {
                        //show error message
                        onEditError(error);
                    },
                },
            );
            //selected
        } else {
            const selectedCheckboxes = Object.keys(dimensionCheckboxObj || {})
                .filter((item) => item !== 'selectAll')
                .map((key: string) => {
                    return isDropdownAtLowestLevel
                        ? dimensionCheckboxObj?.[key]?.pk
                        : dimensionCheckboxObj?.[key]?.attributeNames;
                });
            editKpiMutateFn(
                {
                    ...payload,
                    row_ids: isDropdownAtLowestLevel ? selectedCheckboxes : [],
                    row_filters: !isDropdownAtLowestLevel ? selectedCheckboxes : [],
                },
                {
                    onSuccess: (data) => {
                        onEditSuccess(data?.data, payload);
                        const newBulkUploadInfo: BulkUploadInfo = {
                            status: undefined,
                            errorObject: {},
                        };
                        updateScenarioProperty('bulkUploadInfo', newBulkUploadInfo);
                    },
                    onError: (error) => {
                        //show error message
                        onEditError(error);
                    },
                },
            );
        }
    };

    function infiniteScrollOnNext(loading: boolean) {
        if (!loading) {
            const newOffset = pricingSimulatorConfig?.scenarioTableLimit + (selectedScenarioInfo?.offset ?? 0);
            updateScenarioProperty('offset', newOffset);
        }
    }

    useLayoutEffect(() => {
        if (scrollTopValue > 0 && !commonLoading && selectedScenarioInfo?.data?.length > 0) {
            const targetTable = document.getElementsByClassName('infinite-scroll-component')[0];
            targetTable?.scrollTo?.({
                top: scrollTopValue,
                behavior: 'auto',
            });
            setScrollTopValue(0);
        }
    }, [selectedScenarioInfo?.data, commonLoading]);

    return (
        <div
            className={`px-2 bg-white border-x border-x-borderLinePurpuleAlt items-start h-full ${
                isDefaultView ? 'flex' : ''
            }`}
        >
            <div
                id={scenarioTableContainerId}
                style={{ width: isDefaultView ? Number(widthConfig.dimSection) - 8 : '' }}
            >
                {commonLoading && !selectedScenarioInfo?.offset ? (
                    <div
                        className=" flex flex-col items-center "
                        style={{
                            height: mainSectionHeight.current || 'auto',
                        }}
                    >
                        <Loader />
                    </div>
                ) : !commonLoading && selectedScenarioInfo?.error ? (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: mainSectionHeight.current || 'auto',
                        }}
                    >
                        <ErrorBox msg={showIsEditedRecords ? 'NoEditedRecords' : String(selectedScenarioInfo?.error)} />
                    </div>
                ) : (
                    <>
                        <InfiniteScroller
                            data={selectedScenarioInfo?.data}
                            loading={commonLoading}
                            hasMore={
                                selectedScenarioInfo?.data?.length < (selectedScenarioInfo?.count || 0) || commonLoading
                            }
                            height={
                                selectedScenarioInfo?.data && !selectedScenarioInfo?.error
                                    ? mainSectionHeight.current ?? undefined
                                    : 'auto'
                            }
                            //height={200}
                            //     loader={true}
                            onNext={() => infiniteScrollOnNext(scenarioDataLoading)}
                        >
                            <CustomTable
                                dimLength={selectedDimensionListValues?.length + 2}
                                showSorterTooltip={false}
                                onChange={(pagination, filter, sort: any, extra) => {
                                    //if fixed is not present, it means its coming from kpi column
                                    //for kpi column, show sorting popup
                                    if (!sort?.column?.fixed) {
                                        setSortPopupKey(sort?.column?.sortKey);
                                        return;
                                    }
                                    //only for dim1 and dim2
                                    if (sort?.order) {
                                        const sortMapping: any = {
                                            descend: 'desc',
                                            ascend: 'asc',
                                        };
                                        updateScenarioProperty('sortObject', {
                                            sortBy: sort?.column?.sortKey,
                                            sortingOrder: sortMapping?.[sort?.order] || 'asc',
                                            sortType: 'actual',
                                        });

                                        //default it to dim1 column on cancelling sorting
                                    } else {
                                        updateScenarioProperty('sortObject', {
                                            sortBy: selectedDimensionListValues?.[0] || '',
                                            sortingOrder: 'asc',
                                            sortType: 'actual',
                                        });
                                    }
                                    updateScenarioProperty('offset', 0);
                                }}
                                id={pricingSimulatorTableId}
                                //loading={commonLoading}
                                sticky={{
                                    getContainer: () =>
                                        document.getElementsByClassName(
                                            'infinite-scroll-component',
                                        )?.[0] as HTMLElement,
                                }} //adding x axis scrollbar on table
                                style={{
                                    borderRadius: '4px 0px 0px 4px',
                                    opacity: commonLoading ? '0.3' : 1,
                                    pointerEvents: commonLoading ? 'none' : 'auto',
                                }}
                                locale={{
                                    emptyText: selectedScenarioInfo?.error ? (
                                        <ErrorBox msg={String(selectedScenarioInfo?.error)} />
                                    ) : (
                                        ''
                                    ),
                                }}
                                columns={memoisedColumns}
                                dataSource={[
                                    ...(enableMicroInfoForTables ? [{ key: 'simulator-info-header' }] : []),
                                    ...(selectedScenarioInfo?.data || []),
                                ]}
                                pagination={false}
                                scroll={{ x: '100%' }}
                            />
                        </InfiniteScroller>
                    </>
                )}
            </div>
            {isDefaultView && (
                <div className="flex bg-backgroundGreyAlt5 justify-center flex-col items-center flex-grow h-full">
                    <Result
                        status={'404'}
                        subTitle={'Welcome to Your New Simulation Scenario'}
                        style={{ padding: 0 }}
                    />
                    {/* <span className="text-blackAlt font-lg"> Welcome to Your New Simulation Project</span> */}
                </div>
            )}
            <ScenarioColumnEditModal
                data={handleEditModal}
                loading={editKpiLoading}
                open={handleEditModal.isOpen}
                onCancel={(e) => {
                    setHandleEditModal({ ...modalInitialState });
                }}
                onSubmitClick={(data) => {
                    onEditApply(data);
                }}
            />
        </div>
    );
};

export default ScenarioTableWrapper;
